import { type RefObject, useRef, useState } from 'react';
import { useMediaProgress } from '../hooks/useMediaProgress';
import { formatMediaTime } from '../utils/mediaProgress';

export interface MediaTimeProps {
  mediaRef?: RefObject<HTMLMediaElement | null>;
  source?: string;
  enabled?: boolean;
  value?: number;
  duration?: number;
  format?: 'seconds' | 'clock';
  className?: string;
  currentClassName?: string;
  separatorClassName?: string;
}

export default function MediaTime({
  mediaRef,
  source = '',
  enabled = true,
  value = 0,
  duration = 0,
  format = 'seconds',
  className,
  currentClassName,
  separatorClassName,
}: MediaTimeProps) {
  const [snapshot, setSnapshot] = useState({
    currentTime: 0,
    duration: 0,
    source,
  });
  const previous = useRef('');
  useMediaProgress(
    mediaRef,
    (progress) => {
      const signature = `${source}|${formatMediaTime(progress.currentTime, format)}|${formatMediaTime(progress.duration, format)}`;
      if (previous.current === signature) return;
      previous.current = signature;
      setSnapshot({ ...progress, source });
    },
    enabled,
    source,
  );

  const live =
    snapshot.source === source && enabled
      ? snapshot
      : { currentTime: 0, duration: 0 };
  const currentText = formatMediaTime(
    mediaRef ? live.currentTime : value,
    format,
  );
  const durationText = formatMediaTime(
    live.duration > 0 && mediaRef ? live.duration : duration,
    format,
  );
  const showDuration = !!mediaRef;
  return (
    <span
      className={className}
      style={{
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        className={currentClassName}
        style={{
          display: 'inline-block',
          minWidth: showDuration ? `${durationText.length}ch` : undefined,
        }}
      >
        {currentText}
      </span>
      {showDuration && (
        <>
          <span className={separatorClassName}> / </span>
          <span>{durationText}</span>
        </>
      )}
    </span>
  );
}
